import { TournamentCard } from '../../../components/common/cards';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchTournaments } from '../../../services/tournamentService';
import { getAuctionManagerPath, getOrganizationId } from '../../../utils/organization';
import { getAuctionManagerPath as getClubAuctionManagerPath, getClubId } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';

const normalizeId = (value) => {
  if (!value) return '';

  return value.toString ? value.toString() : `${value}`;
};

const getTournamentRoleFlags = (tournament) => {
  const myRoleFlags = tournament?.myRoleFlags || {};
  return {
    isOwnerForTournament: Boolean(myRoleFlags.isOwnerForTournament),
    isCaptainForTournament: Boolean(myRoleFlags.isCaptainForTournament),
    isPlayerForTournament: Boolean(myRoleFlags.isPlayerForTournament),
  };
};

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const TeamOwnerTournamentsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const organizationId = useMemo(() => getOrganizationId(user?.organization), [user]);
  const clubId = useMemo(() => getClubId(user?.club), [user]);
  const teamId = searchParams.get('teamId');
  const tournamentId = searchParams.get('tournamentId');

  useEffect(() => {
    const loadTournaments = async () => {
      if (!token) return;

      setIsLoading(true);
      setLoadError('');

      try {
        const response = await fetchTournaments(token, organizationId, { teamId, tournamentId, clubId });
        setTournaments(response.tournaments || []);
      } catch (error) {
        setLoadError(error.message || 'Unable to load tournaments.');
      } finally {
        setIsLoading(false);
      }
    };

    loadTournaments();
  }, [clubId, organizationId, teamId, token, tournamentId]);

  const assignedTournaments = useMemo(
    () => (tournaments || []).filter((tournament) => getTournamentRoleFlags(tournament).isOwnerForTournament),
    [tournaments]
  );

  const resolveTeamOwnerPath = (tournament) => {
    const tournamentId = normalizeId(
      tournament?.id || tournament?._id?.toString?.() || tournament?.tournamentId || tournament?._id
    );
    const auctionManagerId = normalizeId(
      tournament?.auctionManager?.id || tournament?.auctionManager?._id || tournament?.auctionManager
    );
    const organizationId = getOrganizationId(tournament?.organization);
    const clubId = getClubId(tournament?.club);
    const auctionManagerPath = clubId
      ? getClubAuctionManagerPath(clubId, auctionManagerId, tournamentId)
      : getAuctionManagerPath(organizationId, auctionManagerId, tournamentId);

    return auctionManagerPath ? `${auctionManagerPath}/online/team-owner-bidding` : '';
  };

  const handleTournamentClick = (tournament) => {
    const destination = resolveTeamOwnerPath(tournament);
    if (destination) {
      navigate(destination);
    }
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="py-4 d-grid gap-3">
      <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-2">
        <div>
          <p className="eyebrow text-primary mb-1">Team owner</p>
          <h1 className="h4 mb-2">Assigned tournaments</h1>
          <p className="text-muted mb-0">
            Open a tournament to join the live bidding room for your team.
          </p>
        </div>
      </div>

      {loadError && <div className="alert alert-danger mb-0">{loadError}</div>}
      {isLoading && <div className="alert alert-secondary mb-0">Loading tournaments...</div>}

      {!isLoading && assignedTournaments.length === 0 && (
        <div className="alert alert-info mb-0">No tournaments are assigned to you yet.</div>
      )}

      {!isLoading &&
        assignedTournaments.map((tournament) => {
          const tournamentId =
            tournament.id || tournament._id?.toString?.() || tournament.tournamentId || tournament._id;
          const destination = resolveTeamOwnerPath(tournament);

          return (
    <PageShell containerClassName="p-0">
            <TournamentCard
              key={tournamentId}
              tournament={tournament}
              onClick={() => handleTournamentClick(tournament)}
              onManage={() => {
                if (destination) {
                  navigate(destination);
                }
              }}
              showActions
            />
          
    </PageShell>
  );
        })}
    </div>
  );
};

export default TeamOwnerTournamentsPage;
